import { authors, stories } from '@/lib/data';
import { getAuthorBySlug, getStoriesByAuthor } from '@/lib/data';
import { notFound } from 'next/navigation';
import AuthorHeader from '@/components/modules/AuthorHeader';
import StoryCard from '@/components/ui/StoryCard';
import SectionHeader from '@/components/ui/SectionHeader';
import LoadMoreButton from '@/components/ui/LoadMoreButton';
import TagPill from '@/components/ui/TagPill';
import type { Metadata } from 'next';

export async function generateStaticParams() {
  return authors.map(author => ({ name: author.slug }));
}

export async function generateMetadata({ params }: { params: Promise<{ name: string }> }): Promise<Metadata> {
  const { name } = await params;
  const author = getAuthorBySlug(name);
  return {
    title: author ? `${author.name} — Veritas News` : 'Author',
    description: author?.bio?.slice(0, 160),
  };
}

export default async function AuthorPage({ params }: { params: Promise<{ name: string }> }) {
  const { name } = await params;
  const author = getAuthorBySlug(name);
  if (!author) notFound();

  const authorStories = getStoriesByAuthor(author.slug);
  const allStories = authorStories.length > 0 ? authorStories : stories.slice(0, 9);
  const featuredStories = allStories.filter(s => ['feature', 'analysis'].includes(s.label || '')).slice(0, 2);
  const latestStories = allStories.slice(0, 9);

  return (
    <div>
      <AuthorHeader author={author} />

      <div className="max-w-[1400px] mx-auto px-5 py-10">
        {/* FEATURED REPORTING */}
        {featuredStories.length > 0 && (
          <section className="mb-10">
            <SectionHeader title="Major Investigations & Features" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredStories.map(story => (
                <StoryCard key={story.id} story={story} size="large" />
              ))}
            </div>
          </section>
        )}

        {/* LATEST ARTICLES */}
        <section className="mb-8">
          <SectionHeader title={`Latest from ${author.name}`} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {latestStories.map(story => (
              <StoryCard key={story.id} story={story} size="medium" />
            ))}
          </div>
          <LoadMoreButton />
        </section>

        {/* TOPICS */}
        <section className="mt-6 pt-6 border-t border-[#D4D4D4]">
          <SectionHeader title="Topics Covered" />
          <div className="flex flex-wrap gap-2">
            {author.topics.map(topic => (
              <TagPill
                key={topic}
                label={topic}
                href={`/topic/${topic.toLowerCase().replace(/ /g, '-')}`}
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
